/**
 * Authentication Service - Firebase Authentication
 *
 * Handles user authentication and admin role management
 */

import { auth, database } from '../firebase';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { ref, get, set } from 'firebase/database';

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User object with role
 */
export const signIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Get user role from database
    const role = await getUserRole(user.uid);

    console.log(`✅ Signed in as ${email} (${role})`);

    return {
      uid: user.uid,
      email: user.email,
      role: role
    };

  } catch (error) {
    console.error('❌ Sign in error:', error);

    // Provide user-friendly error messages
    switch (error.code) {
      case 'auth/user-not-found':
        throw new Error('Utente non trovato');
      case 'auth/wrong-password':
        throw new Error('Password errata');
      case 'auth/invalid-email':
        throw new Error('Email non valida');
      case 'auth/user-disabled':
        throw new Error('Questo account è stato disabilitato');
      default:
        throw new Error('Errore durante il login');
    }
  }
};

/**
 * Sign out current user
 * @returns {Promise<void>}
 */
export const signOutUser = async () => {
  try {
    await signOut(auth);
    console.log('✅ Signed out successfully');
  } catch (error) {
    console.error('❌ Sign out error:', error);
    throw new Error('Errore durante il logout');
  }
};

/**
 * Get user role from database
 * @param {string} uid - User ID
 * @returns {Promise<string>} User role ('admin', 'user', or 'guest')
 */
export const getUserRole = async (uid) => {
  try {
    const roleRef = ref(database, `users/${uid}/role`);
    const snapshot = await get(roleRef);

    if (snapshot.exists()) {
      return snapshot.val();
    }

    // Default role for new users
    return 'user';

  } catch (error) {
    console.error('❌ Error getting user role:', error);
    return 'guest';
  }
};

/**
 * Check if user is admin
 * @param {string} uid - User ID
 * @returns {Promise<boolean>} True if user is admin
 */
export const isAdmin = async (uid) => {
  const role = await getUserRole(uid);
  return role === 'admin';
};

/**
 * Get current authenticated user
 * @returns {Promise<Object|null>} User object or null
 */
export const getCurrentUser = () => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();

      if (user) {
        const role = await getUserRole(user.uid);
        resolve({
          uid: user.uid,
          email: user.email,
          role: role
        });
      } else {
        resolve(null);
      }
    });
  });
};

/**
 * Create new admin user (for initial setup)
 * @param {string} email - Admin email
 * @param {string} password - Admin password
 * @returns {Promise<Object>} Created user
 */
export const createAdmin = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Set admin role in database
    const userRef = ref(database, `users/${user.uid}`);
    await set(userRef, {
      email: user.email,
      role: 'admin',
      createdAt: new Date().toISOString()
    });

    console.log(`✅ Admin user created: ${email}`);

    return {
      uid: user.uid,
      email: user.email,
      role: 'admin'
    };

  } catch (error) {
    console.error('❌ Error creating admin:', error);

    switch (error.code) {
      case 'auth/email-already-in-use':
        throw new Error('Email già in uso');
      case 'auth/weak-password':
        throw new Error('Password troppo debole (minimo 6 caratteri)');
      case 'auth/invalid-email':
        throw new Error('Email non valida');
      default:
        throw new Error('Errore durante la creazione dell\'account');
    }
  }
};

/**
 * Subscribe to auth state changes
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      const role = await getUserRole(user.uid);
      callback({
        uid: user.uid,
        email: user.email,
        role: role
      });
    } else {
      callback(null);
    }
  });
};

const authService = {
  signIn,
  signOutUser,
  getUserRole,
  isAdmin,
  getCurrentUser,
  createAdmin,
  onAuthChange
};

export default authService;
